import { type ChangeEvent, useCallback, useState } from 'react';

import { Input } from '@air/primitive-input';
import { Label } from '@air/primitive-label';

import { SavedFilterEmojiPicker } from '~/components/SavedFilter/components/SavedFilterEmojiPicker';

export type SavedFilterNameInputProps = {
  onEmojiPick: (emoji: string) => void;
  onInputChange: (text: string) => void;
  inputValue: string;
  selectedEmoji?: string;
  onError: (hasError: boolean) => void;
};

export const MAX_SAVED_FILTER_NAME_LENGTH = 65;

export const SavedFilterNameInput = ({
  onEmojiPick,
  onInputChange,
  inputValue,
  selectedEmoji,
  onError,
}: SavedFilterNameInputProps) => {
  const [error, setError] = useState(inputValue.length >= MAX_SAVED_FILTER_NAME_LENGTH);

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const latestInputValue = e.target.value;
      const hasError = latestInputValue.length >= MAX_SAVED_FILTER_NAME_LENGTH;

      onInputChange(latestInputValue);
      setError(hasError);
      onError(hasError);
    },
    [onError, onInputChange],
  );

  return (
    <div>
      <Label htmlFor="filter-name">Name & Emoji</Label>
      <div className="relative mt-1.5 flex w-full items-center justify-evenly">
        <SavedFilterEmojiPicker
          size="medium"
          onEmojiPick={onEmojiPick}
          emoji={selectedEmoji}
          // Safari's Apple Color Emoji is wider than Chrome's; pin to 32×32 so the trigger does not cover the name.
          className="absolute top-1/2 left-1.5 -translate-y-1/2 [&_button]:h-8 [&_button]:w-8"
        />
        <Input
          aria-invalid={error}
          autoFocus
          data-testid="SAVED_FILTER_MODAL_NAME_INPUT"
          name="filter-name"
          onChange={onChange}
          placeholder="Untitled saved search"
          className="w-full min-w-[auto] grow pl-[42px]"
          value={inputValue}
          size="large"
        />
      </div>
      {error && (
        <p className="ads:text-error text-12 text-red-9" data-testid="APP_error">
          Title must be less than 65 characters
        </p>
      )}
    </div>
  );
};
